import React, { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
    Box,
    Button,
    Card,
    CardHeader,
    CardContent,
    CardActions,
    TextField,
    InputAdornment,
    Stack,
    Autocomplete,
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    DialogActions
} from '@mui/material';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';

import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

import { socket } from '../services/socket';

const api = axios.create({
    withCredentials: true
});

interface OutletContextType {
    isConnected: boolean;
    maestroPermissions: string[];
}

interface Clawback {
    clawbackId: number;
    clawbackAmount: number;
    clawbackReason: string;
    clawbackClosed: boolean;
    clawbackHandledInCycle: number | null;
    vendorId: number;
    vendorSourceID: number;
    vendorName: string;
    invoiceId: number;
    invoiceSourceId: number
    eventId: number;
    eventSourceId: number;
    eventName: string;
}

interface VendorOption {
    id: string;
    name: string;
}

interface EventOption {
    eventId: number;
    eventName: string;
    eventSourceId: number;
}

interface InvoiceOption {
    invoiceId: number;
    invoiceSourceId: number;
    events: EventOption[];
}

interface EditedClawbackMap {
        [key: string]: Clawback;
    }

function CommissionsClawback(): JSX.Element {
    const { isConnected, maestroPermissions } =
        useOutletContext() as OutletContextType;

    const { getAccessTokenSilently } = useAuth0();

    const [editedExistingClawbacks, setEditedExistingClawbacks] = useState<EditedClawbackMap>({});
    const [originalExistingClawbacks, setOriginalExistingClawbacks] = useState<Clawback[]>([]);
    const [newClawbackVendor, setNewClawbackVendor] = useState<String>('');
    const [newClawbackEvent, setNewClawbackEvent] = useState<String>('');
    const [newClawbackInvoice, setNewClawbackInvoice] = useState<String>('');
    const [newClawbackAmount, setNewClawbackAmount] = useState('');
    const [newClawbackReason, setNewClawbackReason] = useState('');
    const [openClawbacks, setOpenClawbacks] = useState<Clawback[]>([]);
    const [closedClawbacks, setClosedClawbacks] = useState<Clawback[]>([]);
    const [loadingVendors, setLoadingVendors] = useState(true);
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [selectedClawbackId, setSelectedClawbackId] = useState<number | null>(null);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [eventOptions, setEventOptions] = useState<EventOption[]>([]);
    const [invoiceOptions, setInvoiceOptions] = useState<InvoiceOption[]>([]);
    const [eligibleVendors, setEligibleVendors] = useState<VendorOption[]>([]);
    const [deleteClawbackOpenDialog, setDeleteClawbackOpenDialog] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const isAuthorized = maestroPermissions.includes(`${process.env.REACT_APP_TENANT}:Commissions:Cycles:Update`) &&
        maestroPermissions.includes(`${process.env.REACT_APP_TENANT}:Commissions:Cycles:Read`)

    const handleClawbacksUpdate = useRef(() => {
        // TO DO
    });

    const handleSaveClawbackUpdates = async (id: number) => {
        const clawback = editedExistingClawbacks[String(id)];

        if (!clawback) {
            return;
        }

        await updateExistingClawback(clawback);
    };

    const handleClearClawbackUpdates = (id: number) => {
        const original = originalExistingClawbacks.find(r => r.clawbackId === id);
        if (!original) return;

        setOpenClawbacks(prev =>
            prev.map(c =>
                c.clawbackId === id
                    ? structuredClone(original)
                    : c
            )
        );

        setEditedExistingClawbacks(prev => {
            const copy = { ...prev };
            delete copy[String(id)];
            return copy;
        });
    };

    const handleDeleteClawback = async () => {
        if (selectedClawbackId === null) return;

        await deleteClawback(selectedClawbackId);

        setDeleteClawbackOpenDialog(false);
        setSelectedClawbackId(null);
    };

    const isClawbackDirty = (id: number): boolean => {
        return !!editedExistingClawbacks[String(id)];
    };

    const handleProcessClawbackRowUpdate = (newRow: Clawback): Clawback => {
        if (newRow.clawbackAmount >= 0) {
            toast.error('Clawback amount must be negative');

            const original = originalExistingClawbacks.find(
                c => c.clawbackId === newRow.clawbackId
            );

            return original ? structuredClone(original) : newRow;
        }

        const original = originalExistingClawbacks.find(
            c => c.clawbackId === newRow.clawbackId
        );

        const isDirty =
            original?.clawbackAmount !== newRow.clawbackAmount ||
            original?.clawbackReason !== newRow.clawbackReason;

        setOpenClawbacks(prev =>
            prev.map(c =>
                c.clawbackId === newRow.clawbackId ? newRow : c
            )
        );

        setEditedExistingClawbacks(prev => {
            const copy = { ...prev };

            if (isDirty) {
                copy[String(newRow.clawbackId)] = newRow;
            } else {
                delete copy[String(newRow.clawbackId)];
            }

            return copy;
        });

        return newRow;
    };

    async function deleteClawback(clawbackId: number): Promise<void> {
        try {
            setIsLoading(true);

            const audience =
                process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO;

            const token = await getAccessTokenSilently({
                authorizationParams: { audience },
            });

            await api.delete(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/commissions/clawbacks/${clawbackId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': process.env.REACT_APP_TENANT
                    }
                }
            );

            toast.success('Clawback deleted successfully');
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message ||
                'Failed to delete clawback'
            );
        } finally {
            getClawbackData();
            setIsLoading(false);
        }
    }

    async function getClawbackData(): Promise<void> {
        try {
            setIsLoading(true);

            const audience = process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO;

            const token = await getAccessTokenSilently({
                authorizationParams: { audience },
            });

            const response = await api.get<Clawback[]>(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/commissions/clawbacks`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "x-tenant": process.env.REACT_APP_TENANT
                    }
                }
            );

            const open = response.data.filter(c => !c.clawbackClosed);
            const closed = response.data.filter(c => c.clawbackClosed);

            setOpenClawbacks(open);
            setClosedClawbacks(closed);

            setOriginalExistingClawbacks(structuredClone(open));
        }
        catch (err: any) {
            toast.error(
                err?.response?.data?.message ??
                "Failed to load clawbacks"
            );
        }
        finally {
            setIsLoading(false);
        }
    }

    async function getVendorData(): Promise<void> {
        try {
            setLoadingVendors(true);

            const audience =
                process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO;

            const token = await getAccessTokenSilently({
                authorizationParams: { audience },
            });

            const eligibleVendorsResponse = await api.get(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/revProVendors/commissions/eligible`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': process.env.REACT_APP_TENANT
                    }
                }
            );

            const mappedVendors: VendorOption[] = eligibleVendorsResponse.data.map((v: any) => ({
                id: Number(v.vendorId),
                tnClientBrokerId: Number(v.tnClientBrokerId),
                name: v.companyName
            }));

            setEligibleVendors(mappedVendors);
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message ||
                    'Failed to load vendors'
            );
        } finally {
            setLoadingVendors(false);
        }
    }

    async function getInvoiceData(vendorId: String): Promise<void> {
    try {
        setLoadingInvoices(true);
        setLoadingEvents(true);

        const audience = process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO;

        const token = await getAccessTokenSilently({
            authorizationParams: { audience },
        });

        const response = await api.get(
            `${process.env.REACT_APP_MAESTRO_DOMAIN}/revProVendors/${vendorId}/commissions/clawbacks/eligibleInvoices`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "x-tenant": process.env.REACT_APP_TENANT
                }
            }
        );

        const invoices: InvoiceOption[] = response.data;

        setInvoiceOptions(invoices);

        const uniqueEvents = Array.from(
            new Map(
                invoices
                    .flatMap(invoice => invoice.events)
                    .map(event => [event.eventId, event])
            ).values()
        );

        setInvoiceOptions(invoices);
    }
    catch (err: any) {
        toast.error("Unable to load event and invoice data");
    }
    finally {
        setLoadingInvoices(false);
        setLoadingEvents(false);
    }
}

    async function updateExistingClawback(clawback: Clawback): Promise<void> {
        try {
            setIsLoading(true);

            const audience = process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO;

            const token = await getAccessTokenSilently({
                authorizationParams: { audience },
            });

            const payload = {
                clawbackAmount: clawback.clawbackAmount,
                reason: clawback.clawbackReason,
            };

            await api.put(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/commissions/clawbacks/${clawback.clawbackId}`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "x-tenant": process.env.REACT_APP_TENANT,
                    },
                }
            );

            setOriginalExistingClawbacks(prev =>
                prev.map(c =>
                    c.clawbackId === clawback.clawbackId
                        ? structuredClone(clawback)
                        : c
                )
            );

            setEditedExistingClawbacks(prev => {
                const copy = { ...prev };
                delete copy[String(clawback.clawbackId)];
                return copy;
            });

            toast.success("Clawback updated successfully");
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message ??
                "Failed to update clawback"
            );
        } finally {
            setIsLoading(false);
        }
    }

    async function saveNewClawback(): Promise<void> {
        try {
            setIsLoading(true);

            const audience = process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO;

            const token = await getAccessTokenSilently({
                authorizationParams: { audience },
            });

            const payload = {
                vendorId: newClawbackVendor.toString(),
                invoiceId: newClawbackInvoice.toString(),
                eventId: newClawbackEvent,
                clawbackAmount: Number(newClawbackAmount),
                reason: newClawbackReason
            };

            await api.post(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/commissions/clawbacks`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': process.env.REACT_APP_TENANT
                    }
                }
            );

            toast.success('Clawback created successfully');

            setNewClawbackVendor('');
            setNewClawbackEvent('');
            setNewClawbackInvoice('');
            setNewClawbackAmount('');
            setNewClawbackReason('');

            setEventOptions([]);
            setInvoiceOptions([]);

            getVendorData();
            getClawbackData();

        } catch (err: any) {
            toast.error(
                err?.response?.data?.message ||
                'Failed to save clawback'
            );
        } finally {
            setIsLoading(false);
        }
    }

    const clawbackTableColumns: GridColDef[] = [
        {
            field: 'clawbackId',
            headerName: 'ID',
            width: 80
        },
        {
            field: 'clawbackHandledInCycle',
            headerName: 'Cycle ID',
            width: 80
        },
        {
            field: 'vendorSourceID',
            type: 'string',
            headerName: 'Vendor ID',
            width: 125,
        },
        {
            field: 'vendorName',
            type: 'string',
            headerName: 'Vendor Name',
            width: 125,
        },
        {
            field: 'eventSourceId',
            type: 'string',
            headerName: 'Event ID',
            width: 125,
        },
        {
            field: 'eventName',
            type: 'string',
            headerName: 'Event Name',
            flex: 0.5,
        },
        {
            field: 'invoiceSourceId',
            type: 'string',
            headerName: 'Invoice ID',
            width: 150,
        },
        {
            field: 'clawbackAmount',
            type: 'number',
            headerName: 'Amount',
            width: 150,
            valueFormatter: (value) =>
                new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                }).format(value as number),
            editable: true,
        },
        {
            field: 'clawbackReason',
            type: 'string',
            headerName: 'Reason',
            width: 150,
            editable: true,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 260,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,

            renderCell: (params) => {
                const id = params.row.clawbackId;
                const isDirty = isClawbackDirty(id);

                return (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={!isDirty}
                            onClick={() => handleSaveClawbackUpdates(id)}
                        >
                            Save
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={!isDirty}
                            onClick={() => handleClearClawbackUpdates(id)}
                        >
                            Clear
                        </Button>
                        <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => {
                                setSelectedClawbackId(id);
                                setDeleteClawbackOpenDialog(true);
                            }}
                        >
                            Delete
                        </Button>
                    </Box>
                );
            }
        }
    ];

    const openClawbackColumns = clawbackTableColumns
    .filter(column => column.field !== 'clawbackHandledInCycle');

    const closedClawbackColumns = clawbackTableColumns
        .filter(column => column.field !== 'actions')
        .map(column => {
            if (
                column.field === 'clawbackAmount' ||
                column.field === 'clawbackReason'
            ) {
                return {
                    ...column,
                    editable: false,
                };
            }

            return column;
        });

    useEffect(() => {
        if (!isConnected && !isAuthorized) return;

        getClawbackData();
        getVendorData();

        const handler = handleClawbacksUpdate.current;

        socket.on('vendor', handler);

        return () => {
            socket.off('vendor', handler);
        };
    }, [isConnected]);

    useEffect(() => {
        if (!newClawbackInvoice) {
            setNewClawbackAmount('');
        }
    }, [newClawbackInvoice]);

    if (!isAuthorized) {
        return (
            <Box sx={{ textAlign: 'center', p: 3 }}>
                <WarningRoundedIcon />
                <p>Not Authorized</p>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Dialog
                open={deleteClawbackOpenDialog}
                onClose={() => setDeleteClawbackOpenDialog(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    Delete Clawback
                </DialogTitle>
                <DialogContent
                    sx={{
                        minHeight: 125,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                    }}
                >
                    <Typography sx={{ fontSize: '13pt' }}>
                        Once DELETE is clicked, this clawback will be deleted
                        <br />
                        <Box
                            component="span"
                            sx={{
                                color: 'error.main',
                                fontWeight: 'bold'
                            }}
                        >
                            THIS ACTION CANNOT BE UNDONE.
                        </Box>
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteClawbackOpenDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => handleDeleteClawback()}
                        sx={{
                            backgroundColor: '#29bc35d1',
                            color: '#103cff',
                            fontWeight: 600,
                            '&:hover': {
                                backgroundColor: '#C8E6C9'
                            }
                        }}
                    >
                        Delete Clawback
                    </Button>
                </DialogActions>
            </Dialog>
            <Box sx={{ mb: 3 }}>
                <Card>
                    <CardHeader
                        title="Add New Clawback"
                    />
                    <CardContent>
                        <Stack spacing={2}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 2,
                                            flexWrap: 'wrap'
                                        }}
                                    >
                                        <Box sx={{ flex: .25 }}>
                                            <Autocomplete
                                            options={eligibleVendors}
                                            getOptionLabel={(option) => option.name}
                                            loading={loadingVendors}
                                            value={
                                                eligibleVendors.find(
                                                    v => v.id.toString() === newClawbackVendor
                                                ) || null
                                            }
                                            onChange={(_, selectedVendor) => {
                                                const vendorId = selectedVendor?.id.toString() || '';

                                                setNewClawbackVendor(vendorId);

                                                setNewClawbackInvoice('');
                                                setNewClawbackEvent('');

                                                setInvoiceOptions([]);
                                                setEventOptions([]);

                                                if (vendorId) {
                                                    getInvoiceData(vendorId);
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Select Vendor"
                                                />
                                            )}
                                        />
                                            <TextField
                                                fullWidth
                                                sx={{
                                                    mt: 2
                                                }}
                                                label="Clawback Amount (Negative Value)"
                                                disabled={
                                                    !newClawbackEvent
                                                }
                                                value={newClawbackAmount}
                                                onChange={(e) => {
                                                    const value = e.target.value;

                                                    if (/^-?\d*\.?\d{0,2}$/.test(value) && !/^\d/.test(value)) {
                                                        setNewClawbackAmount(value);
                                                    }
                                                }}
                                                slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                $
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                    htmlInput: {
                                                        inputMode: 'decimal',
                                                    },
                                                }}
                                            />
                                        </Box>
                                        <Box sx={{ flex: .25, minWidth: 250 }}>
                                            <Autocomplete
                                            fullWidth
                                            options={invoiceOptions}
                                            getOptionLabel={(option) => option.invoiceSourceId.toString()}
                                            disabled={
                                                loadingInvoices ||
                                                !newClawbackVendor ||
                                                invoiceOptions.length === 0
                                            }
                                            value={
                                                invoiceOptions.find(
                                                    invoice => invoice.invoiceId.toString() === newClawbackInvoice
                                                ) || null
                                            }
                                            onChange={(_, selectedInvoice) => {
                                                const invoiceId = selectedInvoice?.invoiceId.toString() || '';

                                                setNewClawbackInvoice(invoiceId);

                                                if (!selectedInvoice) {
                                                    setEventOptions([]);
                                                    setNewClawbackEvent('');
                                                    return;
                                                }

                                                const events = selectedInvoice.events;

                                                setEventOptions(events);

                                                if (events.length === 1) {
                                                    setNewClawbackEvent(events[0].eventId.toString());
                                                } else {
                                                    setNewClawbackEvent('');
                                                }
                                            }}
                                            isOptionEqualToValue={(option, value) =>
                                                option.invoiceId === value.invoiceId
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Select Invoice"
                                                />
                                            )}
                                        />
                                        <TextField
                                            fullWidth
                                            sx={{ mt: 2 }}
                                            label="Clawback Reason"
                                            disabled={!newClawbackEvent}
                                            value={newClawbackReason}
                                            onChange={(e) => setNewClawbackReason(e.target.value)}
                                            multiline
                                            rows={2}
                                        />
                                    </Box>
                                    <Box sx={{ flex: .50, minWidth: 250 }}>
                                            <Autocomplete
                                                options={eventOptions}
                                                getOptionLabel={(event) => event.eventName.toString()}
                                                disabled={
                                                    loadingEvents ||
                                                    !newClawbackInvoice ||
                                                    eventOptions.length === 0
                                                }
                                                value={
                                                    eventOptions.find(
                                                        e => e.eventId.toString() === newClawbackEvent
                                                    ) || null
                                                }
                                                onChange={(_, selectedEvent) => {
                                                    setNewClawbackEvent(
                                                        selectedEvent?.eventId.toString() || ''
                                                    );
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Select Event"
                                                    />
                                                )}
                                            />
                                        </Box>
                                    </Box>
                                </CardContent>
                                <CardActions>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        disabled={
                                            !newClawbackVendor ||
                                            !newClawbackEvent ||
                                            !newClawbackInvoice ||
                                            !newClawbackAmount ||
                                            !newClawbackReason
                                        }
                                        onClick={saveNewClawback}
                                    >
                                        Save
                                    </Button>
                                </CardActions>
                            </Card>
                        </Stack>
                    </CardContent>
                </Card>
            </Box>
            <Box
                sx={{
                    length: '50%',
                    height: 300,
                    mb: 3
                }}
            >
                <Card>
                    <CardHeader
                    title="Open Clawbacks"
                />
                    <CardContent sx={{ height: 200 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DataGridPro
                                getRowId={(row) => row.clawbackId}
                                rows={openClawbacks}
                                columns={openClawbackColumns}
                                disableRowSelectionOnClick
                                loading={isLoading}
                                hideFooter
                                pageSizeOptions={[10, 25, 50]}
                                processRowUpdate={handleProcessClawbackRowUpdate}
                                sx={{ height: '100%' }}
                                rowHeight={35}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 10,
                                            page: 0
                                        }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </CardContent>
                </Card>
            </Box>
            <Box
                sx={{
                    length: '50%',
                    height: 300,
                    mb: 3
                }}
            >
                <Card>
                    <CardHeader
                    title="Closed Clawbacks"
                />
                    <CardContent sx={{ height: 200 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DataGridPro
                                getRowId={(row) => row.clawbackId}
                                rows={closedClawbacks}
                                columns={closedClawbackColumns}
                                disableRowSelectionOnClick
                                loading={isLoading}
                                hideFooter
                                pageSizeOptions={[10, 25, 50]}
                                rowHeight={35}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 10,
                                            page: 0
                                        }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}

export default CommissionsClawback;